import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  SalonModerationRequest,
  SalonModerationResult,
  SalonPlanGrantRequest,
  SalonPlanGrantResult,
  SalonsResponse,
} from '@manaro/contracts'
import { queryKeys } from '@/shared/api/query-keys.ts'
import { fetchSalons, grantPlan, moderateSalon } from './api.ts'
import { applyModeration } from './moderation.ts'
import { applyPlanGrant } from './plan-grant.ts'

export function useSalons() {
  return useQuery({
    queryKey: queryKeys.salons,
    queryFn: ({ signal }) => fetchSalons({ signal }),
    staleTime: 5 * 60 * 1000,
  })
}

export function useRefreshSalons() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => fetchSalons({ refresh: true }),
    onSuccess: (data: SalonsResponse) => queryClient.setQueryData(queryKeys.salons, data),
  })
}

export function useModerateSalon() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: SalonModerationRequest }) => moderateSalon(id, request),
    // The list is heavy to recount: patch the one salon in place instead of refetching everything.
    onSuccess: (result: SalonModerationResult) =>
      queryClient.setQueryData<SalonsResponse>(queryKeys.salons, (data) => data && applyModeration(data, result)),
    // A conflict or a lost answer means the list is out of date: reload it so the dialog shows the real state.
    onError: () => queryClient.invalidateQueries({ queryKey: queryKeys.salons }),
  })
}

export function useGrantPlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: SalonPlanGrantRequest }) => grantPlan(id, request),
    onSuccess: (result: SalonPlanGrantResult) =>
      queryClient.setQueryData<SalonsResponse>(queryKeys.salons, (data) => data && applyPlanGrant(data, result)),
    // A paid period or a lost answer means the list is out of date: reload it.
    onError: () => queryClient.invalidateQueries({ queryKey: queryKeys.salons }),
  })
}
